import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { TabBar } from './components/TabBar';
import { PageTransition } from './components/PageTransition';
import { AuthModal } from './components/AuthModal';
import { AuthGate } from './components/AuthGate';
import { ForgotPasswordModal } from './components/ForgotPasswordModal';
import { VerifyEmailModal } from './components/VerifyEmailModal';
import { Home } from './pages/Home';
import { BrowseListings } from './pages/BrowseListings';
import { ItemDetail } from './pages/ItemDetail';
import { BookingFlow } from './pages/BookingFlow';
import { EditBooking } from './pages/EditBooking';
import { Trips } from './pages/Trips';
import { TripDetail } from './pages/TripDetail';
import { StartRental } from './pages/StartRental';
import { EndRental } from './pages/EndRental';
import { CreateListing } from './pages/CreateListing';
import { Chat } from './pages/Chat';
import { Profile } from './pages/Profile';
import { ProfileGuest } from './pages/ProfileGuest';
import { Review } from './pages/Review';
import { Dispute } from './pages/Dispute';
import { Inventory } from './pages/Inventory';
import { AccountSettings } from './pages/AccountSettings';
import { SupportCenter } from './pages/SupportCenter';
import { OwnerProfile } from './pages/OwnerProfile';
import { getCurrentUser, logout, User, updateUserProfile } from './utils/auth';
type View = 'home' | 'browse-listings' | 'item-detail' | 'owner-profile' | 'booking-flow' | 'edit-booking' | 'trips' | 'trip-detail' | 'start-rental' | 'end-rental' | 'create-listing' | 'chat' | 'profile' | 'review' | 'dispute' | 'inventory' | 'account-settings' | 'support-center';
export interface BookingRequest {
  id: string;
  itemTitle: string;
  itemImage: string;
  startDate: Date;
  endDate: Date;
  pickupTime: string;
  dropoffTime: string;
  ownerNote: string;
  submittedAt: Date;
  expiresAt: Date;
  status: 'pending' | 'approved' | 'active' | 'completed';
  location: string;
  pricing: {
    rental: number;
    deposit: number;
    total: number;
  };
}
export function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('home');
  const [currentView, setCurrentView] = useState<View>('home');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [profileInitialTab, setProfileInitialTab] = useState('Listings');
  const [previousView, setPreviousView] = useState<View>('home');
  const [currentBooking, setCurrentBooking] = useState<BookingRequest | null>(null);
  const [bookings, setBookings] = useState<BookingRequest[]>([]);
  const [bookingStarted, setBookingStarted] = useState(false);
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [completedTripIds, setCompletedTripIds] = useState<string[]>([]);
  // Auth modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [authGateAction, setAuthGateAction] = useState<'book' | 'chat' | 'list' | 'activity'>('book');
  const [authContext, setAuthContext] = useState<string>('');
  // Check authentication on mount
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);
  const isAuthenticated = currentUser !== null;
  const handleAuthSuccess = () => {
    const user = getCurrentUser();
    setCurrentUser(user);
  };
  const handleProfileUpdate = (updatedUser: User) => {
    setCurrentUser(updatedUser);
  };
  const handleLogout = () => {
    logout();
    setCurrentUser(null);
    setCurrentView('home');
    setActiveTab('home');
  };
  const requireAuth = (action: 'book' | 'chat' | 'list' | 'activity', context: string = '') => {
    if (!isAuthenticated) {
      setAuthGateAction(action);
      setAuthContext(context);
      setShowAuthGate(true);
      return false;
    }
    return true;
  };
  // Navigation handlers
  const handleItemClick = (id: string) => {
    setSelectedItemId(id);
    setCurrentView('item-detail');
  };
  const handleOwnerClick = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setCurrentView('owner-profile');
    setActiveTab('activity');
  };
  const handleBackToHome = () => {
    setCurrentView('home');
    setActiveTab('home');
  };
  const handleActivityHub = () => {
    if (!requireAuth('activity', 'Sign in to view your bookings and activity')) return;
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleMyListings = () => {
    if (!requireAuth('list', 'Sign in to view and manage your listings')) return;
    setProfileInitialTab('Listings');
    setCurrentView('profile');
    setActiveTab('my-stuff');
  };
  const handleBrowseNearby = () => {
    setSelectedCategory(undefined);
    setCurrentView('browse-listings');
  };
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setCurrentView('browse-listings');
  };
  const handleStartBooking = () => {
    if (!requireAuth('book', 'Sign in to request a booking')) return;
    setPreviousView(currentView);
    setCurrentView('booking-flow');
    setActiveTab('activity');
  };
  const handleBookingComplete = (bookingData: BookingRequest) => {
    setBookings([...bookings, bookingData]);
    setCurrentBooking(bookingData);
    setCurrentView('chat');
    setActiveTab('activity');
  };
  const handleEditBooking = () => {
    setCurrentView('edit-booking');
    setActiveTab('activity');
  };
  const handleSaveBooking = (updatedBooking: BookingRequest) => {
    setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b));
    setCurrentBooking(updatedBooking);
    setCurrentView('chat');
    setActiveTab('activity');
  };
  const handleCancelBooking = () => {
    if (currentBooking) {
      setBookings(bookings.filter(b => b.id !== currentBooking.id));
      setCurrentBooking(null);
    }
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleTripClick = (tripId: string) => {
    setSelectedTripId(tripId);
    setCurrentView('trip-detail');
    setActiveTab('activity');
  };
  const handleBackToTrips = () => {
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleStartRental = () => {
    setCurrentView('start-rental');
    setActiveTab('activity');
  };
  const handleStartRentalComplete = () => {
    setBookingStarted(true);
    setCurrentView('chat');
    setActiveTab('activity');
  };
  const handleEndRental = () => {
    setCurrentView('end-rental');
    setActiveTab('activity');
  };
  const handleEndRentalComplete = () => {
    // Mark booking as returned/completed from borrower side
    if (currentBooking) {
      const updatedBooking = {
        ...currentBooking,
        status: 'completed' as const
      };
      setBookings(bookings.map(b => b.id === updatedBooking.id ? updatedBooking : b));
      setCurrentBooking(updatedBooking);
    }
    setBookingCompleted(true);
    setCurrentView('chat');
    setActiveTab('activity');
  };
  const handleReviewComplete = () => {
    // Mark the current booking as completed
    if (currentBooking) {
      const completedBooking = {
        ...currentBooking,
        status: 'completed' as const
      };
      setBookings(bookings.map(b => b.id === completedBooking.id ? completedBooking : b));
    }
    // Track completed trip ID (for mock data transitions)
    if (selectedTripId) {
      setCompletedTripIds(prev => [...prev, selectedTripId]);
    }
    // Reset all booking flow state
    setCurrentBooking(null);
    setBookingStarted(false);
    setBookingCompleted(false);
    // Navigate to trips list
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleOpenChat = () => {
    if (!requireAuth('chat', 'Sign in to message the owner')) return;
    setPreviousView(currentView);
    setCurrentView('chat');
    setActiveTab('activity');
  };
  const handleBackFromChat = () => {
    setCurrentView(previousView);
    if (previousView === 'home' || previousView === 'browse-listings') {
      setActiveTab('home');
    } else {
      setActiveTab('activity');
    }
  };
  const handleBackFromBooking = () => {
    setCurrentView(previousView);
    if (previousView === 'home' || previousView === 'browse-listings') {
      setActiveTab('home');
    } else {
      setActiveTab('activity');
    }
  };
  const handleCreateListing = () => {
    if (!requireAuth('list', 'Sign in to list your items')) return;
    setCurrentView('create-listing');
  };
  const handleListingComplete = () => {
    setCurrentView('inventory');
  };
  const handleBackToProfile = () => {
    setCurrentView('profile');
    setActiveTab('my-stuff');
  };
  const handleOpenDispute = () => {
    setCurrentView('dispute');
    setActiveTab('activity');
  };
  const handleDisputeComplete = () => {
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleNavigateToBookings = () => {
    setCurrentView('trips');
    setActiveTab('activity');
  };
  const handleNavigateToInventory = () => {
    setCurrentView('inventory');
  };
  const handleNavigateToSettings = () => {
    setCurrentView('account-settings');
  };
  const handleNavigateToSupport = () => {
    setCurrentView('support-center');
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'home') {
      setCurrentView('home');
    } else if (tab === 'activity') {
      if (!requireAuth('activity', 'Sign in to view your activity')) return;
      setCurrentView('trips');
    } else if (tab === 'list') {
      if (!requireAuth('list', 'Sign in to list items')) return;
      setCurrentView('create-listing');
    } else if (tab === 'my-stuff') {
      if (!requireAuth('list', 'Sign in to manage your stuff')) return;
      setCurrentView('profile');
    } else if (tab === 'settings') {
      if (!requireAuth('activity', 'Sign in to access settings')) return;
      setCurrentView('account-settings');
    }
  };
  return <div className="min-h-screen bg-cream" data-id="element-0">
      <AnimatePresence mode="wait" data-id="element-1">
        {/* Home Flow */}
        {currentView === 'home' && <PageTransition key="home" direction="none" data-id="element-2">
            <Home onItemClick={handleItemClick} onBrowseNearby={handleBrowseNearby} onCategoryClick={handleCategoryClick} data-id="element-3" />
          </PageTransition>}

        {/* Browse Listings */}
        {currentView === 'browse-listings' && <PageTransition key="browse-listings" direction="forward" data-id="element-4">
            <BrowseListings category={selectedCategory} onBack={handleBackToHome} onItemClick={handleItemClick} data-id="element-5" />
          </PageTransition>}

        {/* Item Detail & Booking Flow - Now part of Activity */}
        {currentView === 'item-detail' && <PageTransition key="item-detail" direction="forward" data-id="element-6">
            <ItemDetail itemId={selectedItemId || ''} onBack={() => {
          if (selectedCategory) {
            setCurrentView('browse-listings');
            setActiveTab('home');
          } else {
            handleBackToHome();
          }
        }} onBooking={handleStartBooking} onChat={handleOpenChat} onOwnerClick={() => handleOwnerClick('owner-1')} data-id="element-7" />
          </PageTransition>}

        {/* Owner Profile */}
        {currentView === 'owner-profile' && <PageTransition key="owner-profile" direction="forward" data-id="element-8">
            <OwnerProfile ownerId={selectedOwnerId || ''} onBack={() => setCurrentView('item-detail')} onItemClick={handleItemClick} data-id="element-9" />
          </PageTransition>}

        {currentView === 'booking-flow' && isAuthenticated && <PageTransition key="booking-flow" direction="forward" data-id="element-10">
            <BookingFlow itemId={selectedItemId || ''} onBack={handleBackFromBooking} onComplete={handleBookingComplete} data-id="element-11" />
          </PageTransition>}

        {currentView === 'edit-booking' && currentBooking && isAuthenticated && <PageTransition key="edit-booking" direction="forward" data-id="element-12">
              <EditBooking bookingId={currentBooking.id} currentBooking={currentBooking} onBack={() => setCurrentView('chat')} onSave={handleSaveBooking} onCancel={handleCancelBooking} data-id="element-13" />
            </PageTransition>}

        {/* Trips Flow */}
        {currentView === 'trips' && isAuthenticated && <PageTransition key="trips" direction="none" data-id="element-14">
            <Trips bookings={bookings} onTripClick={handleTripClick} onStartBooking={tripId => {
          setSelectedTripId(tripId);
          handleStartRental();
        }} onReviewInspect={tripId => {
          setSelectedTripId(tripId);
          setCurrentView('review');
          setActiveTab('activity');
        }} completedTripIds={completedTripIds} data-id="element-15" />
          </PageTransition>}

        {currentView === 'trip-detail' && isAuthenticated && <PageTransition key="trip-detail" direction="forward" data-id="element-16">
            <TripDetail tripId={selectedTripId || ''} onBack={handleBackToTrips} onStartRental={handleStartRental} onEndRental={handleEndRental} onChat={handleOpenChat} data-id="element-17" />
          </PageTransition>}

        {currentView === 'start-rental' && isAuthenticated && <PageTransition key="start-rental" direction="forward" data-id="element-18">
            <StartRental tripId={selectedTripId || ''} onBack={() => setCurrentView('trip-detail')} onComplete={handleStartRentalComplete} data-id="element-19" />
          </PageTransition>}

        {currentView === 'end-rental' && isAuthenticated && <PageTransition key="end-rental" direction="forward" data-id="element-20">
            <EndRental tripId={selectedTripId || ''} onBack={() => setCurrentView('chat')} onComplete={handleEndRentalComplete} onSubmitClaim={() => {
          setCurrentView('dispute');
          setActiveTab('activity');
        }} data-id="element-21" />
          </PageTransition>}

        {/* Review Flow */}
        {currentView === 'review' && isAuthenticated && <PageTransition key="review" direction="forward" data-id="element-22">
            <Review tripId={selectedTripId || ''} onBack={handleBackToTrips} onComplete={handleReviewComplete} onSubmitClaim={() => {
          setCurrentView('dispute');
          setActiveTab('activity');
        }} data-id="element-23" />
          </PageTransition>}

        {/* Dispute Flow */}
        {currentView === 'dispute' && isAuthenticated && <PageTransition key="dispute" direction="forward" data-id="element-24">
            <Dispute tripId={selectedTripId || ''} onBack={handleBackToTrips} onComplete={handleDisputeComplete} data-id="element-25" />
          </PageTransition>}

        {/* Create Listing Flow */}
        {currentView === 'create-listing' && isAuthenticated && <PageTransition key="create-listing" direction="forward" data-id="element-26">
            <CreateListing onBack={handleBackToProfile} onComplete={handleListingComplete} onListAnother={() => {
          // Stay on create-listing view (form resets internally)
        }} onViewListings={() => {
          setCurrentView('inventory');
        }} onMyActivity={() => {
          setCurrentView('trips');
          setActiveTab('activity');
        }} data-id="element-27" />
          </PageTransition>}

        {/* Chat */}
        {currentView === 'chat' && isAuthenticated && <PageTransition key="chat" direction="forward" data-id="element-28">
            <Chat bookingId={selectedTripId || selectedItemId || ''} currentBooking={currentBooking} onBack={handleBackFromChat} onRequestBooking={!currentBooking ? handleStartBooking : undefined} onEditRequest={currentBooking?.status === 'pending' ? handleEditBooking : undefined} onStartRental={currentBooking?.status === 'approved' || currentBooking?.status === 'active' ? handleStartRental : undefined} onEndRental={bookingStarted && !bookingCompleted ? handleEndRental : undefined} onReviewInspect={bookingCompleted ? () => {
          setCurrentView('review');
          setActiveTab('activity');
        } : undefined} bookingStarted={bookingStarted} bookingCompleted={bookingCompleted} data-id="element-29" />
          </PageTransition>}

        {/* Profile / My Stuff - Logged In */}
        {currentView === 'profile' && isAuthenticated && <PageTransition key="profile" direction="none" data-id="element-30">
            <Profile currentUser={currentUser} onProfileUpdate={handleProfileUpdate} onCreateListing={handleCreateListing} onItemClick={handleItemClick} onNavigateToBookings={handleNavigateToBookings} onNavigateToInventory={handleNavigateToInventory} onNavigateToSettings={handleNavigateToSettings} onNavigateToSupport={handleNavigateToSupport} initialTab={profileInitialTab} onLogout={handleLogout} data-id="element-31" />
          </PageTransition>}

        {/* Profile - Guest (shown for my-stuff or settings when not authenticated) */}
        {currentView === 'profile' && !isAuthenticated && <PageTransition key="profile-guest" direction="none" data-id="element-32">
            <ProfileGuest onSignIn={() => setShowAuthModal(true)} data-id="element-33" />
          </PageTransition>}

        {/* Account Pages */}
        {currentView === 'inventory' && isAuthenticated && <PageTransition key="inventory" direction="forward" data-id="element-34">
            <Inventory onBack={handleBackToProfile} onCreateListing={handleCreateListing} data-id="element-35" />
          </PageTransition>}

        {currentView === 'account-settings' && isAuthenticated && <PageTransition key="account-settings" direction="forward" data-id="element-36">
            <AccountSettings onBack={handleBackToProfile} data-id="element-37" />
          </PageTransition>}

        {/* Settings - Guest */}
        {currentView === 'account-settings' && !isAuthenticated && <PageTransition key="settings-guest" direction="none" data-id="element-38">
            <ProfileGuest onSignIn={() => setShowAuthModal(true)} data-id="element-39" />
          </PageTransition>}

        {currentView === 'support-center' && isAuthenticated && <PageTransition key="support-center" direction="forward" data-id="element-40">
            <SupportCenter onBack={handleBackToProfile} data-id="element-41" />
          </PageTransition>}
      </AnimatePresence>

      {/* Tab Bar - Always visible */}
      <TabBar activeTab={activeTab} onTabChange={handleTabChange} data-id="element-42" />

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} onForgotPassword={() => {
      setShowAuthModal(false);
      setShowForgotPassword(true);
    }} onSignupSuccess={email => {
      setPendingEmail(email);
      setShowAuthModal(false);
      setShowVerifyEmail(true);
    }} context={authContext} data-id="element-43" />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} onSuccess={() => {
      setShowForgotPassword(false);
      setShowAuthModal(true);
    }} data-id="element-44" />

      {/* Verify Email Modal */}
      <VerifyEmailModal isOpen={showVerifyEmail} onClose={() => setShowVerifyEmail(false)} onSuccess={handleAuthSuccess} email={pendingEmail} data-id="element-45" />

      {/* Auth Gate */}
      <AuthGate isOpen={showAuthGate} onClose={() => setShowAuthGate(false)} onSignIn={() => {
      setShowAuthGate(false);
      setShowAuthModal(true);
    }} action={authGateAction} data-id="element-46" />
    </div>;
}